import { useState, useRef } from "react";
import { useFetch } from "@/hooks/useFetch";
import api, { apiError } from "@/lib/api";
import { Loading, ErrorState, Empty } from "@/components/DataState";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { MapPin, Plus, Pencil, Trash2, Loader2, LocateFixed } from "lucide-react";

const EMPTY = { name: "", address: "", lat: "", lng: "", radius_m: "500" };

function AddressAutocomplete({ value, onChange, onPick }) {
  const [sug, setSug] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const skip = useRef(false);

  const handle = (text) => {
    onChange(text);
    if (skip.current) { skip.current = false; return; }
    if (timer.current) clearTimeout(timer.current);
    if (!text || text.trim().length < 4) { setSug([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get("/geocode/search", { params: { q: text.trim() } });
        setSug(res.data?.results || []);
        setOpen(true);
      } catch (e) {
        setSug([]);
      } finally {
        setLoading(false);
      }
    }, 600);
  };

  const pick = (s) => {
    skip.current = true;
    onPick({ address: s.display_name, lat: s.lat, lng: s.lon });
    setOpen(false);
    setSug([]);
  };

  return (
    <div className="relative">
      <Input
        value={value}
        onChange={(e) => handle(e.target.value)}
        onFocus={() => sug.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Digite o endereço (mín. 4 caracteres)"
        autoComplete="off"
        data-testid="base-address"
        className="border-slate-700 bg-slate-950 text-slate-100"
      />
      {loading && <div className="absolute right-3 top-2.5"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>}
      {open && sug.length > 0 && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-slate-700 bg-slate-900 shadow-xl" data-testid="base-address-suggestions">
          {sug.map((s, i) => (
            <button
              type="button"
              key={i}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              data-testid={`base-address-sug-${i}`}
              className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-800"
            >
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-orange-400" />
              <span>{s.display_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Bases() {
  const { user } = useAuth();
  const manage = can(user, "bases_manage");
  const { data, loading, error, refetch } = useFetch("/admin/bases");
  const [form, setForm] = useState(null);
  const [del, setDel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);

  if (loading) return <Loading />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  const bases = data?.bases || [];
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const useCurrentLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocalização não suportada neste navegador."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, lat: String(pos.coords.latitude), lng: String(pos.coords.longitude) }));
        setLocating(false);
        toast.success("Localização atual preenchida");
      },
      (err) => { setLocating(false); toast.error("Não foi possível obter a localização: " + err.message); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const openNew = () => setForm({ ...EMPTY });
  const openEdit = (b) =>
    setForm({ id: b.id, name: b.name || "", address: b.address || "", lat: String(b.lat ?? ""), lng: String(b.lng ?? ""), radius_m: String(b.radius_m ?? "500") });

  const save = async () => {
    const payload = {
      name: form.name.trim(),
      address: form.address.trim(),
      lat: Number(form.lat),
      lng: Number(form.lng),
      radius_m: parseInt(form.radius_m, 10),
    };
    if (!payload.name || !payload.address || isNaN(payload.lat) || isNaN(payload.lng) || !payload.radius_m) {
      toast.error("Preencha nome, endereço, latitude, longitude e raio.");
      return;
    }
    setBusy(true);
    try {
      if (form.id) await api.put(`/admin/bases/${form.id}`, payload);
      else await api.post("/admin/bases", payload);
      toast.success(form.id ? "Base atualizada" : "Base criada");
      setForm(null);
      await refetch();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/bases/${del.id}`);
      toast.success("Base excluída");
      setDel(null);
      await refetch();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="bases">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-100">Bases</h2>
          <p className="text-sm text-slate-500">{bases.length} base(s) cadastrada(s)</p>
        </div>
        {manage && (
          <Button onClick={openNew} className="bg-orange-500 text-white hover:bg-orange-600" data-testid="base-new-btn">
            <Plus className="h-4 w-4" /> Nova base
          </Button>
        )}
      </div>

      {bases.length === 0 ? (
        <Empty label="Nenhuma base cadastrada" />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {bases.map((b) => (
            <div key={b.id} className="rounded-xl border border-slate-800 bg-slate-900 p-4" data-testid={`base-card-${b.id}`}>
              <div className="flex items-start gap-2">
                <div className="mt-0.5 rounded-lg bg-orange-500/15 p-2 text-orange-300"><MapPin className="h-4 w-4" /></div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-slate-100">{b.name}</p>
                  <p className="mt-0.5 text-xs text-slate-400 line-clamp-2">{b.address}</p>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                <span className="font-mono">{Number(b.lat).toFixed(5)}, {Number(b.lng).toFixed(5)}</span>
                <span>raio {b.radius_m} m</span>
              </div>
              {manage && (
                <div className="mt-3 flex justify-end gap-1.5 border-t border-slate-800 pt-3">
                  <Button size="sm" variant="ghost" className="h-8 px-2 text-slate-300 hover:text-white" onClick={() => openEdit(b)} data-testid={`base-edit-${b.id}`}>
                    <Pencil className="h-4 w-4" /> Editar
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 px-2 text-rose-300 hover:text-rose-200" onClick={() => setDel(b)} data-testid={`base-delete-${b.id}`}>
                    <Trash2 className="h-4 w-4" /> Excluir
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-md border-slate-800 bg-slate-900">
          <DialogHeader>
            <DialogTitle className="text-slate-100">{form?.id ? "Editar base" : "Nova base"}</DialogTitle>
            <DialogDescription className="text-slate-400">Defina o ponto e o raio de tolerância (metros).</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-slate-300">Nome</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="base-name" className="border-slate-700 bg-slate-950 text-slate-100" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300">Endereço</Label>
                <AddressAutocomplete
                  value={form.address}
                  onChange={(t) => set("address", t)}
                  onPick={({ address, lat, lng }) => setForm((f) => ({ ...f, address, lat: String(lat), lng: String(lng) }))}
                />
                <p className="text-[10px] text-slate-500" data-testid="base-osm-attribution">Busca de endereços por © OpenStreetMap contributors</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-slate-300">Latitude</Label>
                  <Input type="number" step="any" value={form.lat} onChange={(e) => set("lat", e.target.value)} data-testid="base-lat" className="border-slate-700 bg-slate-950 text-slate-100" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-300">Longitude</Label>
                  <Input type="number" step="any" value={form.lng} onChange={(e) => set("lng", e.target.value)} data-testid="base-lng" className="border-slate-700 bg-slate-950 text-slate-100" />
                </div>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={useCurrentLocation} disabled={locating}
                className="w-full border-slate-700 text-slate-300 hover:text-white" data-testid="base-use-location">
                <LocateFixed className="h-4 w-4" /> {locating ? "Obtendo localização..." : "Usar localização atual"}
              </Button>
              <div className="space-y-1.5">
                <Label className="text-slate-300">Raio (metros)</Label>
                <Input type="number" min="1" value={form.radius_m} onChange={(e) => set("radius_m", e.target.value)} data-testid="base-radius" className="border-slate-700 bg-slate-950 text-slate-100" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" className="text-slate-400" onClick={() => setForm(null)}>Cancelar</Button>
            <Button className="bg-orange-500 text-white hover:bg-orange-600" disabled={busy} onClick={save} data-testid="base-save">
              {form?.id ? "Salvar" : "Criar base"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <DialogContent className="max-w-sm border-slate-800 bg-slate-900">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Excluir base</DialogTitle>
            <DialogDescription className="text-slate-400">Excluir "{del?.name}"? Esta ação não pode ser desfeita.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" className="text-slate-400" onClick={() => setDel(null)}>Cancelar</Button>
            <Button className="bg-rose-600 text-white hover:bg-rose-700" disabled={busy} onClick={remove} data-testid="base-delete-confirm">
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
